<template>
  <div class="page-wrapper">
    <section class="relative overflow-hidden bg-background">
      <div class="px-[5%]">
        <div class="w-full max-w-7xl mx-auto pt-8 md:pt-12 pb-8">
          <div class="flex flex-col gap-6">
            <div class="flex items-center justify-end gap-4">
              <p class="text-sm text-muted-foreground">
                {{ searchSummary }}
              </p>
              <div class="relative">
                <UiButton
                  v-if="!isSearchOpen"
                  variant="outline"
                  class="gap-2"
                  @click="openSearch"
                >
                  <Icon name="tabler:search" size="16" />
                  Search
                </UiButton>
                <div v-else class="relative w-full min-w-55 sm:min-w-80">
                  <Icon
                    name="tabler:search"
                    size="18"
                    class="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  />
                  <UiInput
                    ref="searchInputRef"
                    v-model="searchQuery"
                    type="search"
                    placeholder="Search blog articles"
                    class="pl-10 pr-10"
                    aria-label="Search blog articles"
                    @blur="onSearchBlur"
                  />
                  <UiButton
                    variant="ghost"
                    size="icon"
                    class="absolute right-1 top-1/2 -translate-y-1/2"
                    @click="closeSearch"
                  >
                    <Icon name="tabler:x" size="16" />
                  </UiButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-background">
      <div class="px-[5%]">
        <div class="w-full max-w-7xl mx-auto py-12 md:py-16 space-y-16">
          <div v-if="hasResults" class="space-y-16">
            <div class="grid lg:grid-cols-[2fr_1fr] gap-8">
              <NuxtLink
                v-if="mainPost"
                :to="`/blog/${mainPost.slug}`"
                class="group block"
              >
                <div class="overflow-hidden rounded-2xl border bg-card shadow-sm transition-all group-hover:shadow-lg">
                  <div class="relative aspect-video bg-muted">
                    <NuxtImg
                      v-if="mainPostImage"
                      :src="mainPostImage.src"
                      :alt="mainPostImage.alt"
                      class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div
                      v-else
                      class="h-full w-full bg-muted"
                    />
                  </div>
                  <div class="p-6 md:p-8 space-y-4">
                    <div class="flex flex-wrap items-center gap-2">
                      <UiBadge variant="secondary" class="text-xs">
                        {{ mainPost.displayCategory || 'Article' }}
                      </UiBadge>
                      <span class="text-xs text-muted-foreground">
                        {{ formatDate(mainPost.publishedDate) }}
                      </span>
                      <span
                        v-if="mainPost.readingTime"
                        class="text-xs text-muted-foreground"
                      >
                        • {{ mainPost.readingTime }} min read
                      </span>
                    </div>
                    <div class="space-y-2">
                      <h2 class="text-2xl md:text-3xl font-semibold text-foreground">
                        {{ mainPost.title }}
                      </h2>
                      <p class="text-muted-foreground line-clamp-3">
                        {{ mainPost.description }}
                      </p>
                    </div>
                    <div class="flex flex-wrap gap-2">
                      <UiBadge
                        v-for="tag in mainPost.tags?.slice(0, 4)"
                        :key="tag"
                        variant="outline"
                        class="text-xs"
                      >
                        {{ tag }}
                      </UiBadge>
                    </div>
                  </div>
                </div>
              </NuxtLink>

              <div class="grid gap-6">
                <SearchCard
                  v-for="post in secondaryPosts"
                  :key="post.slug"
                  :title="post.title"
                  :description="post.description"
                  :published-date="post.publishedDate"
                  :category="post.displayCategory || 'Article'"
                  :link-to="`/blog/${post.slug}`"
                  content-type="Blog"
                  content-type-icon="tabler:file-text"
                  :reading-time="post.readingTime"
                  :tags="post.tags"
                />
              </div>
            </div>

            <section v-if="recommendedPosts.length" class="space-y-6">
              <div class="flex items-center justify-between">
                <div>
                  <h2 class="text-2xl font-semibold text-foreground">
                    Recommended
                  </h2>
                  <p class="text-sm text-muted-foreground">
                    Editor picks and community favorites.
                  </p>
                </div>
              </div>
              <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                <SearchCard
                  v-for="post in recommendedPosts"
                  :key="post.slug"
                  :title="post.title"
                  :description="post.description"
                  :published-date="post.publishedDate"
                  :category="post.displayCategory || 'Article'"
                  :link-to="`/blog/${post.slug}`"
                  content-type="Blog"
                  content-type-icon="tabler:star"
                  :reading-time="post.readingTime"
                  :tags="post.tags"
                  :trending="true"
                />
              </div>
            </section>

            <section class="space-y-6">
              <div class="flex items-center justify-between">
                <div>
                  <h2 class="text-2xl font-semibold text-foreground">
                    Latest articles
                  </h2>
                  <p class="text-sm text-muted-foreground">
                    Fresh updates from the Bubba team.
                  </p>
                </div>
              </div>
              <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                <SearchCard
                  v-for="post in latestPosts"
                  :key="post.slug"
                  :title="post.title"
                  :description="post.description"
                  :published-date="post.publishedDate"
                  :category="post.displayCategory || 'Article'"
                  :link-to="`/blog/${post.slug}`"
                  content-type="Blog"
                  content-type-icon="tabler:file-text"
                  :reading-time="post.readingTime"
                  :tags="post.tags"
                />
              </div>
            </section>

            <section v-for="section in tagSections" :key="section.tag" class="space-y-6">
              <div class="flex items-center justify-between gap-4">
                <div>
                  <h3 class="text-xl font-semibold text-foreground">
                    {{ section.tag }}
                  </h3>
                  <p class="text-sm text-muted-foreground">
                    {{ $tc('blog.article', section.posts.length) }}
                  </p>
                </div>
                <NuxtLink
                  :to="{ path: '/blog', query: { search: section.tag } }"
                  class="text-sm text-primary hover:text-primary/80 transition-colors"
                >
                  See all
                </NuxtLink>
              </div>
              <div class="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                <SearchCard
                  v-for="post in section.posts"
                  :key="post.slug"
                  :title="post.title"
                  :description="post.description"
                  :published-date="post.publishedDate"
                  :category="post.displayCategory || 'Article'"
                  :link-to="`/blog/${post.slug}`"
                  content-type="Blog"
                  content-type-icon="tabler:file-text"
                  :reading-time="post.readingTime"
                  :tags="post.tags"
                />
              </div>
            </section>
          </div>

          <div v-else class="py-16 text-center">
            <h2 class="text-2xl font-semibold text-foreground mb-2">
              No articles found
            </h2>
            <p class="text-muted-foreground">
              Try a different search term or explore the latest updates.
            </p>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import type { Collections } from '@nuxt/content';
import {
  definePageMeta,
  defineWebPage,
  queryCollection,
  useAsyncData,
  useI18n,
  useRoute,
  useRouter,
  useSchemaOrg,
  useSeoMeta,
} from '#imports';
import { useBreadcrumbSchema } from '#composables/useBreadcrumbSchema';

import SearchCard from '#components/pages/search/SearchCard.vue';
import type { BlogPost, ContentPost } from '#types/search';

definePageMeta({
  layout: 'content',
});

useSchemaOrg([
  defineWebPage({ '@type': 'CollectionPage' }),
]);

useBreadcrumbSchema([
  { name: 'Home', item: '/' },
  { name: 'Blog' },
]);

const router = useRouter();
const route = useRoute();
const { $ts, $tc, $getLocale } = useI18n();

const searchQuery = ref((route.query.search as string) || '');
const isSearchOpen = ref(Boolean(searchQuery.value));
const searchInputRef = ref<HTMLInputElement | null>(null);

const openSearch = async () => {
  isSearchOpen.value = true;
  await nextTick();
  searchInputRef.value?.focus();
};

const closeSearch = () => {
  if (!searchQuery.value.trim()) {
    isSearchOpen.value = false;
  }
};

const onSearchBlur = () => {
  closeSearch();
};

const petTypeTags = [
  'Dogs',
  'Cats',
  'Birds',
  'Fish',
  'Reptiles',
  'Small Pets',
  'Rabbits',
  'Hamsters',
  'Horses',
];

const currentLocale = 'en';

const { data: contentPosts } = await useAsyncData(
  `blog-posts-${currentLocale}`,
  () => {
    const collectionName = `content_${currentLocale}` as keyof Collections;
    return queryCollection(collectionName).all();
  },
);

const allPosts = computed(() => {
  if (!contentPosts.value) return [];

  return (contentPosts.value as ContentPost[])
    .filter((post) => post.path.includes('/blog/'))
    .map((post) => {
      const slug = post.path.split('/').pop() || '';

      let readingTime = 5;
      if (post.readingTime) {
        readingTime =
          typeof post.readingTime === 'number' ? post.readingTime : 5;
      } else if (post.readTime) {
        const match = post.readTime.match(/(\d+)/);
        readingTime = match && match[1] ? parseInt(match[1], 10) : 5;
      }

      return {
        slug,
        title: post.title || 'Untitled',
        description: post.description || '',
        publishedDate:
          post.date || post.publishedDate || new Date().toISOString().split('T')[0],
        category: '',
        displayCategory: post.category || 'Uncategorized',
        tags: Array.isArray(post.tags) ? post.tags : [],
        readingTime,
        published: post.published !== false && post._draft !== true,
        path: post.path,
        image: post.image,
        recommended: post.recommended === true,
      } as BlogPost;
    });
});

const publishedPosts = computed(() =>
  allPosts.value.filter((post) => post.published),
);

const searchFilteredPosts = computed(() => {
  if (!searchQuery.value.trim()) {
    return publishedPosts.value;
  }

  const query = searchQuery.value.toLowerCase();
  return publishedPosts.value.filter((post) => {
    return (
      post.title.toLowerCase().includes(query) ||
      post.description.toLowerCase().includes(query) ||
      post.tags?.some((tag) => tag.toLowerCase().includes(query)) ||
      post.displayCategory?.toLowerCase().includes(query)
    );
  });
});

const sortedPosts = computed(() =>
  [...searchFilteredPosts.value].sort(
    (a, b) => new Date(b.publishedDate).getTime() - new Date(a.publishedDate).getTime(),
  ),
);

const mainPost = computed(() => sortedPosts.value[0] || null);
const secondaryPosts = computed(() => sortedPosts.value.slice(1, 3));

const primarySlugs = computed(() =>
  new Set(
    [mainPost.value, ...secondaryPosts.value]
      .filter((post): post is BlogPost => Boolean(post))
      .map((post) => post.slug),
  ),
);

const recommendedPosts = computed(() => {
  const recommended = sortedPosts.value.filter((post) => post.recommended);
  const source = recommended.length > 0 ? recommended : sortedPosts.value;
  return source
    .filter((post) => !primarySlugs.value.has(post.slug))
    .slice(0, 4);
});

const excludedSlugs = computed(() =>
  new Set(
    [...primarySlugs.value, ...recommendedPosts.value.map((post) => post.slug)],
  ),
);

const latestPosts = computed(() =>
  sortedPosts.value
    .filter((post) => !excludedSlugs.value.has(post.slug))
    .slice(0, 6),
);

const tagSections = computed(() => {
  const tagMap = new Map<string, BlogPost[]>();

  searchFilteredPosts.value.forEach((post) => {
    post.tags?.forEach((tag) => {
      if (!tagMap.has(tag)) {
        tagMap.set(tag, []);
      }
      const list = tagMap.get(tag);
      if (list && list.length < 3) {
        list.push(post);
      }
    });
  });

  const orderedPetTags = petTypeTags.filter((tag) => tagMap.has(tag));
  const remainingTags = Array.from(tagMap.keys())
    .filter((tag) => !petTypeTags.includes(tag))
    .sort((a, b) => a.localeCompare(b));

  return [...orderedPetTags, ...remainingTags]
    .map((tag) => ({ tag, posts: tagMap.get(tag) || [] }))
    .filter((section) => section.posts.length > 0);
});

const hasResults = computed(() => searchFilteredPosts.value.length > 0);

const searchSummary = computed(() => {
  const total = searchFilteredPosts.value.length;
  if (!searchQuery.value.trim()) {
    return $tc('blog.articlesAvailable', total);
  }
  return $ts('blog.searchResult', { query: searchQuery.value.trim(), count: total });
});

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString($getLocale(), {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const mainPostImage = computed(() => {
  if (!mainPost.value?.image) return null;
  if (typeof mainPost.value.image === 'string') {
    return { src: mainPost.value.image, alt: mainPost.value.title };
  }
  return {
    src: mainPost.value.image.src,
    alt: mainPost.value.image.alt || mainPost.value.title,
  };
});

watch(
  () => searchQuery.value,
  (value) => {
    if (value.trim()) {
      isSearchOpen.value = true;
    }
    const query = value.trim() ? { search: value.trim() } : {};
    router.replace({ query });
  },
);

useSeoMeta({
  title: () => $ts('blog.seo.title'),
  description: () => $ts('blog.seo.description'),
});

</script>
